/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { appsCreateApp } from "../funcs/appsCreateApp.js";
import { appsDeleteApp } from "../funcs/appsDeleteApp.js";
import { appsGetApp } from "../funcs/appsGetApp.js";
import { appsListApps } from "../funcs/appsListApps.js";
import { appsUpdateApp } from "../funcs/appsUpdateApp.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import * as components from "../models/components/index.js";
import * as operations from "../models/operations/index.js";
import { unwrapAsync } from "../types/fp.js";
import { PageIterator, unwrapResultIterator } from "../types/operations.js";

export class Apps extends ClientSDK {
  /**
   * Create app
   *
   * @remarks
   * Create an app within a project. The app is created with default `production` and `preview` environments.
   *
   * Set exactly one source. Use `git` to create a GitHub-sourced app and connect its repository. Use `oci` to create an app from a prebuilt OCI image.
   *
   * The slug you provide is the stable, caller-defined handle used to reference this app. It must be unique within the project.
   *
   * **Important**: The slug cannot collide with an existing app in the same project. A duplicate slug returns a 409 conflict.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `project.*.create_app` (to create apps in any project)
   * - `project.<project_id>.create_app` (to create apps in a specific project)
   */
  async createApp(
    request: components.V1AppsCreateAppRequestBodyUnion,
    options?: RequestOptions,
  ): Promise<components.V1AppsCreateAppResponseBody> {
    return unwrapAsync(appsCreateApp(
      this,
      request,
      options,
    ));
  }

  /**
   * Delete app
   *
   * @remarks
   * Delete an existing app, identified by its id.
   *
   * Deletion is asynchronous and eventually consistent. The app and all of its associated resources (environments, deployments, custom domains) are torn down by a background workflow. A successful response indicates the deletion was enqueued, not that every resource has already been removed.
   *
   * Apps with delete protection enabled cannot be deleted until protection is disabled.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `app.*.delete_app` (to delete any app)
   * - `app.<app_id>.delete_app` (to delete a specific app)
   */
  async deleteApp(
    request: components.V1AppsDeleteAppRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1AppsDeleteAppResponseBody> {
    return unwrapAsync(appsDeleteApp(
      this,
      request,
      options,
    ));
  }

  /**
   * Get app
   *
   * @remarks
   * Retrieve a single app by its id or slug within a project.
   *
   * Use this to fetch app details after creation or to verify an app exists before performing operations.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `app.*.read_app` (to read any app)
   * - `app.<app_id>.read_app` (to read a specific app)
   */
  async getApp(
    request: components.V1AppsGetAppRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1AppsGetAppResponseBody> {
    return unwrapAsync(appsGetApp(
      this,
      request,
      options,
    ));
  }

  /**
   * List apps
   *
   * @remarks
   * Retrieve a paginated list of apps within a project.
   *
   * Use this to enumerate every app in a project. Results are ordered by app id and paginated; when `hasMore` is true, pass the returned `cursor` to fetch the next page.
   *
   * **Required Permissions**
   *
   * Your root key must have the following permission:
   * - `app.*.read_app` (to read apps in any project)
   */
  async listApps(
    request: components.V1AppsListAppsRequestBody,
    options?: RequestOptions,
  ): Promise<
    PageIterator<operations.AppsListAppsResponse, { cursor: string }>
  > {
    return unwrapResultIterator(appsListApps(
      this,
      request,
      options,
    ));
  }

  /**
   * Update app
   *
   * @remarks
   * Update an existing app, identified by its id.
   *
   * The app name, slug, default branch, and delete protection setting can be changed. Omitted fields are left unchanged. Changing the slug affects the deployment domains generated for this app.
   *
   * **Important**: The slug cannot collide with an existing app in the same project. A duplicate slug returns a 409 conflict.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `app.*.update_app` (to update any app)
   * - `app.<app_id>.update_app` (to update a specific app)
   */
  async updateApp(
    request: components.V1AppsUpdateAppRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1AppsUpdateAppResponseBody> {
    return unwrapAsync(appsUpdateApp(
      this,
      request,
      options,
    ));
  }
}
